import React from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Flyout from '@divvy-web/skylab.flyout'
import ListItem from '@divvy-web/skylab.listitem'
import Tag from '@divvy-web/skylab.tag'
import { TOAST_TYPE_BASIC, TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useRemoveInvitedUsers } from '@sputnik/components/InviteCollaborators/Collaborator/RemoveInvitedUser.gql'
import type { NewCollaborator as NewCollaboratorData } from '@sputnik/hooks/useApplicationCollaborators/types'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

const getDisplayRole = (role?: string | null) => {
  switch (role) {
    case 'admin':
      return (
        <FormattedMessage
          defaultMessage='Admin'
          id='sputnik.Collaborator__iHN12u'
        />
      )
    case 'authorizedSigner':
      return (
        <FormattedMessage
          defaultMessage='Authorized Signer'
          id='sputnik.Collaborator__7D4/eQ'
        />
      )
    case 'companyOwner':
      return (
        <FormattedMessage
          defaultMessage='Company Owner'
          id='sputnik.Collaborator__1qoWbv'
        />
      )
    case 'outsourcedAccountant':
      return (
        <FormattedMessage
          defaultMessage='Outsourced Accountant'
          id='sputnik.Collaborator__n+1Q83'
        />
      )
    case 'collaborator':
      return (
        <FormattedMessage
          defaultMessage='Collaborator'
          id='sputnik.Collaborator__P1AKC5'
        />
      )
    default:
      return null
  }
}

export interface NewCollaboratorProps {
  appId: string
  isRemoveDisabled?: boolean
  handleEditCollaborator?: () => void
  onDeleteCollaborator?: () => void
  collaborator: NewCollaboratorData
}

const NewCollaborator = ({
  appId,
  collaborator,
  isRemoveDisabled,
  handleEditCollaborator,
  onDeleteCollaborator,
}: NewCollaboratorProps) => {
  const [getClassName, makeTestId] = useNameFormatter('NewCollaborator')
  const showToast = useToast()
  const { formatMessage } = useIntl()

  const { email, hasLoggedIn, firstName, lastName, role, isInvited } = collaborator

  const [removeInvitedUser, { loading }] = useRemoveInvitedUsers({
    refetchQueries: ['GetApplicationEmails'],
    onCompleted: () => {
      logInfo('removeCollaborator', {
        attributes: {
          creditAppId: appId,
          result: 'collaborator removed',
        },
        eventName: 'removeCollaboratorSuccess',
      })
      onDeleteCollaborator?.()

      showToast(
        TOAST_TYPE_BASIC,
        <FormattedMessage
          defaultMessage='Collaborator removed'
          id='sputnik.Collaborator__avltpZ'
        />,
        { autoHideDelay: 3000 },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'removeCollaborator',
          creditAppId: appId,
          message: error?.message,
          result: 'failed to remove collaborator',
        },
        eventName: 'removeCollaboratorFailure',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove collaborator'
          id='sputnik.Collaborator__wMhKK7'
        />,
      )
    },
  })

  const fullName = firstName && lastName ? `${firstName} ${lastName}` : email

  const onDelete = async () => {
    await removeInvitedUser({
      variables: {
        creditApplicationId: appId,
        emails: [email],
      },
    })
  }

  return (
    <div
      className={styles[getClassName('')]}
      data-testid='NewCollaborator-row'
    >
      <ListItem
        caption={
          isInvited ? (
            <span className={styles[getClassName('caption')]}>
              {hasLoggedIn ? (
                <FormattedMessage
                  defaultMessage='Invitation accepted'
                  id='sputnik.Collaborator__IqaxjA'
                />
              ) : (
                <FormattedMessage
                  defaultMessage='Invitation sent'
                  id='sputnik.Collaborator__sjdyQ0'
                />
              )}
            </span>
          ) : null
        }
        label={fullName}
        tag={<Tag type='primary'>{getDisplayRole(role)}</Tag>}
      >
        <Flyout
          isPortal
          renderMenu={({ hide }) => (
            <div className={styles[getClassName('menu')]}>
              <ListItem
                iconName='edit'
                label={
                  <FormattedMessage
                    defaultMessage='Edit'
                    id='sputnik.Collaborator__wEQDC6'
                  />
                }
                onClick={() => {
                  handleEditCollaborator?.()
                  hide?.()
                }}
              />
              <ListItem
                className={styles[getClassName('delete-button')]}
                iconName='trash'
                isDisabled={loading}
                label={formatMessage({ defaultMessage: 'Remove', id: 'sputnik.Collaborator__G/yZLu' })}
                onClick={() => {
                  onDelete()
                  hide?.()
                }}
              />
            </div>
          )}
          renderTrigger={({ toggle, ref }) => {
            if (!isInvited || isRemoveDisabled) return null
            return (
              <BasicButton
                ref={ref}
                dataTestId={makeTestId(`more-button-${email}`)}
                icon='more-vertical'
                name='more-button'
                showSpinner={loading}
                type={BASIC_BUTTON_TYPE_FLAT}
                onClick={(e) => toggle?.(e as React.MouseEvent<HTMLButtonElement, MouseEvent>)}
              />
            )
          }}
        />
      </ListItem>
    </div>
  )
}

export default NewCollaborator
